import React from 'react';
import { XIcon } from '@phosphor-icons/react';
import Button from './Button';
import { Step } from 'react-joyride';

interface JoyrideTooltipProps {
  step: Step;
  tooltipProps: any;
  primaryProps: any;
  skipProps: any;
  closeProps: any;
  backProps: any;
  size: number;
  isLastStep: boolean;
  index: number;
  continuous: boolean;
}

const JoyrideTooltip: React.FC<JoyrideTooltipProps> = ({
  step,
  tooltipProps,
  primaryProps,
  skipProps,
  closeProps,
  backProps,
  size,
  isLastStep,
  index,
  continuous
}) => {
  const stepNumber = index + 1;
  const totalSteps = size;
  const isFirstStep = index === 0;

  return (
    <div {...tooltipProps} className="relative bg-white rounded-lg p-6 w-[90vw] max-w-md">
      {/*
        Dismiss affordance, on every step including the last.
        Wired to skipProps, not closeProps: react-joyride's close() only advances the
        index (status stays 'running' unless it lands past the last step), so on a
        continuous tour an X on closeProps would behave as a second Next button. skip()
        sets status 'skipped', which is what the tour callbacks listen for to stop the
        tour and persist the "already seen" flag.
      */}
      <button
        type="button"
        onClick={skipProps.onClick}
        aria-label="Close tour"
        title="Close tour"
        className="absolute top-3 right-3 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-blue"
      >
        <XIcon size={18} />
      </button>

      {/* Progress indicator - hide for single-step tours */}
      {totalSteps > 1 && (
        <div className="flex items-center justify-between mb-4 pr-8">
          <span className="text-sm text-gray-500">
            Step {stepNumber} of {totalSteps}
          </span>
          <div className="flex space-x-1">
            {Array.from({ length: totalSteps }).map((_, stepIndex) => (
              <div
                key={stepIndex}
                className={`h-2 w-2 rounded-full ${stepIndex + 1 === stepNumber ? 'bg-secondary-blue' : 'bg-gray-300'}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Content. Single-step tours have no progress row above, so the content itself
          has to clear the absolutely-positioned dismiss button. */}
      <div className={totalSteps > 1 ? 'mb-6' : 'mb-6 pr-8'}>
        {typeof step.content === 'string' ? (
          <p className="text-content-default leading-relaxed">{step.content}</p>
        ) : (
          <div>{step.content}</div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-between">
        <div className="flex space-x-2">
          {/* Back button */}
          {!isFirstStep && (
            <Button {...backProps} variant="outline" size="md">
              Back
            </Button>
          )}

          {/* Skip button */}
          {!isLastStep && (
            <Button {...skipProps} variant="ghost" size="md">
              Skip tour
            </Button>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Close button (for non-continuous tours, but not on final step) */}
          {!continuous && !isLastStep && (
            <Button {...closeProps} variant="outline" size="md">
              Close
            </Button>
          )}

          {/* Primary action button */}
          <Button {...primaryProps} variant="primary" size="md" className="min-w-[80px]">
            {isLastStep ? 'Finish' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JoyrideTooltip;
